import React, { useCallback, useMemo } from 'react';
import { Alert, Button, Col, Form, Input, InputNumber, Radio, Row } from 'antd';
import { DollarOutlined } from '@ant-design/icons';

function CustomerServiceCommissionAllocationFields({
    item,
    doctors = [],
    sales = [],
    assistants = [],
    recipientRoles = [],
    styles,
    onChangeItem,
}) {
    const updateItem = useCallback((nextData) => {
        onChangeItem('commission_allocation_data', nextData, item.id);
    }, [item.id, onChangeItem]);

    const allocationGroups = useMemo(() => {
        const sources = {
            DOCTOR_ASSIGNMENT: { selected: item.doctors || [], options: doctors, allowWorkUnit: true },
            SALES_ASSIGNMENT: { selected: item.sales || [], options: sales, allowWorkUnit: false },
            ASSISTANT_ASSIGNMENT: { selected: item.assistants || [], options: assistants, allowWorkUnit: true },
        };

        return (recipientRoles || []).map((role) => {
            const source = sources[role.assignment_source];
            if (!source) return null;
            return {
                role_key: role.role_key,
                label: role.label || role.name || role.role_key,
                allow_work_unit: source.allowWorkUnit,
                employees: source.selected.map((id) => {
                    const employee = (source.options || []).find((x) => Number(x.user_id) === Number(id));
                    return { id, display_name: employee?.display_name || employee?.full_name || `#${id}` };
                }),
            };
        }).filter((group) => group && group.employees.length > 0);
    }, [item.doctors, item.sales, item.assistants, doctors, sales, assistants, recipientRoles]);

    const renderSection = useCallback((title, icon, content) => (
        <div style={styles.blockCard}>
            <div style={styles.blockTitle}>{icon} <span style={{ marginLeft: 8 }}>{title}</span></div>
            {content}
        </div>
    ), [styles]);

    const getAllocationMode = useCallback((roleKey) => {
        const row = (item.allocation_modes || []).find((x) => x.role_key === roleKey);
        return row?.allocation_mode || 'equal_split';
    }, [item.allocation_modes]);

    const updateAllocationMode = useCallback((roleKey, mode) => {
        const modes = [...(item.allocation_modes || [])];
        const idx = modes.findIndex((x) => x.role_key === roleKey);

        if (idx >= 0) {
            modes[idx] = {
                ...modes[idx],
                allocation_mode: mode,
                is_manual_override: true,
            };
        } else {
            modes.push({
                role_key: roleKey,
                allocation_mode: mode,
                is_manual_override: true,
                note: '',
            });
        }

        updateItem({
            allocation_modes: modes,
            allocation_agreements: item.allocation_agreements || [],
            work_units: item.work_units || [],
        });
    }, [item, updateItem]);

    const addAllocationAgreement = useCallback((roleKey) => {
        const exists = (item.allocation_agreements || []).some(
            (ag) => ag.role_key === roleKey
        );

        if (exists) return;

        updateItem({
            allocation_modes: item.allocation_modes || [],
            work_units: item.work_units || [],
            allocation_agreements: [
                ...(item.allocation_agreements || []),
                {
                    role_key: roleKey,
                    scope_type: 'full_service',
                    revenue_amount: null,
                    note: '',
                    items: [],
                },
            ],
        });
    }, [item, updateItem]);

    const updateAllocationAgreement = useCallback((agreementIndex, field, value) => {
        const agreements = [...(item.allocation_agreements || [])];

        agreements[agreementIndex] = {
            ...agreements[agreementIndex],
            [field]: value,
        };

        updateItem({
            allocation_modes: item.allocation_modes || [],
            work_units: item.work_units || [],
            allocation_agreements: agreements,
        });
    }, [item, updateItem]);

    const removeAllocationAgreement = useCallback((agreementIndex) => {
        updateItem({
            allocation_modes: item.allocation_modes || [],
            work_units: item.work_units || [],
            allocation_agreements: (item.allocation_agreements || []).filter((_, idx) => idx !== agreementIndex),
        });
    }, [item, updateItem]);

    const updateAllocationAgreementItem = useCallback((agreementIndex, roleKey, employeeId, percent) => {
        const agreements = [...(item.allocation_agreements || [])];
        const agreement = {
            ...(agreements[agreementIndex] || {}),
        };
        const rows = [...(agreement.items || [])];

        const idx = rows.findIndex((x) => Number(x.employee_id) === Number(employeeId));

        if (idx >= 0) {
            rows[idx] = {
                ...rows[idx],
                role_key: roleKey,
                percent: Number(percent) || 0,
            };
        } else {
            rows.push({
                employee_id: Number(employeeId),
                role_key: roleKey,
                percent: Number(percent) || 0,
            });
        }

        agreement.role_key = roleKey;
        agreement.items = rows;
        agreements[agreementIndex] = agreement;

        updateItem({
            allocation_modes: item.allocation_modes || [],
            work_units: item.work_units || [],
            allocation_agreements: agreements,
        });
    }, [item, updateItem]);

    const updateWorkUnit = useCallback((roleKey, employeeId, field, value) => {
        const rows = [...(item.work_units || [])];

        const idx = rows.findIndex(
            (x) => Number(x.employee_id) === Number(employeeId) && x.role_key === roleKey
        );

        if (idx >= 0) {
            rows[idx] = {
                ...rows[idx],
                [field]: value,
            };
        } else {
            rows.push({
                employee_id: Number(employeeId),
                role_key: roleKey,
                unit_type: field === 'unit_type' ? value : 'session',
                unit_value: field === 'unit_value' ? Number(value) || 0 : 0,
                revenue_scope: field === 'revenue_scope' ? value : 'private',
                note: '',
            });
        }

        updateItem({
            allocation_modes: item.allocation_modes || [],
            allocation_agreements: item.allocation_agreements || [],
            work_units: rows,
        });
    }, [item, updateItem]);

    const validGroups = allocationGroups.filter((group) => {
        const mode = getAllocationMode(group.role_key);
        return group.employees.length >= 2 || mode !== 'equal_split';
    });

    if (!validGroups.length) {
        return renderSection(
            'Phân bổ doanh thu tính hoa hồng',
            <DollarOutlined style={{ color: '#1677ff' }} />,
            <Alert
                type="warning"
                message="Mỗi bộ phận phải có ít nhất 2 nhân sự mới có thể thiết lập phân bổ doanh thu."
            />
        );
    }

    return (
        <>
            {validGroups.map((group) => {
                const mode = getAllocationMode(group.role_key);
                const agreements = (item.allocation_agreements || []).filter(
                    (ag) => ag.role_key === group.role_key
                );
                const employeeIds = group.employees.map(employee => Number(employee.id));
                const staleItems = agreements.flatMap(agreement => agreement.items || [])
                    .filter(row => !employeeIds.includes(Number(row.employee_id)));

                return renderSection(
                    `Phân bổ doanh thu tính hoa hồng cho ${group.label.toLowerCase()}`,
                    <DollarOutlined style={{ color: '#1677ff' }} />,
                    <div key={group.role_key}>
                        {staleItems.length > 0 && <Alert
                            type="warning"
                            message={`Cấu hình ${group.label.toLowerCase()} đang tham chiếu ${staleItems.length} nhân viên không còn thuộc role. Vui lòng chuyển sang chia đều hoặc cập nhật lại tỷ lệ trước khi lưu.`}
                            style={{ marginBottom: 16 }}
                        />}
                        <Form.Item label="Phương thức phân bổ">
                            <Radio.Group
                                value={mode}
                                onChange={(e) => updateAllocationMode(group.role_key, e.target.value)}
                            >
                                <Radio value="agreement_percent">Theo tỷ lệ thỏa thuận</Radio>

                                {group.allow_work_unit && (
                                    <Radio value="work_unit">Theo phần việc thực tế</Radio>
                                )}

                                <Radio value="equal_split">Chia đều</Radio>
                            </Radio.Group>
                        </Form.Item>

                        {mode === 'equal_split' && (
                            <Alert
                                type="warning"
                                message={`Hệ thống sẽ chia đều doanh thu cho các ${group.label.toLowerCase()} được chọn.`}
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        {mode === 'agreement_percent' && (
                            <div>
                                <div style={{ fontWeight: 700, marginBottom: 12 }}>
                                    Thỏa thuận phân bổ doanh thu tính hoa hồng - {group.label}
                                </div>

                                <div style={styles.fieldBox}>
                                    {agreements.map((agreement) => {
                                        const realIndex = (item.allocation_agreements || []).findIndex(
                                            (x) => x === agreement
                                        );

                                        const totalPercent = (agreement.items || []).filter(
                                            row => employeeIds.includes(Number(row.employee_id))
                                        ).reduce(
                                            (sum, x) => sum + (Number(x.percent) || 0),
                                            0
                                        );

                                        return (
                                            <div
                                                key={realIndex}
                                                style={{
                                                    border: '1px dashed #d1d5db',
                                                    borderRadius: 10,
                                                    padding: 12,
                                                    marginBottom: 12,
                                                    background: '#fff',
                                                }}
                                            >
                                                <Row gutter={[16, 0]}>
                                                    {/* <Col xs={24} md={12}>
                                                        <Form.Item label="Phạm vi thỏa thuận">
                                                            <Radio.Group
                                                                value={agreement.scope_type || 'full_service'}
                                                                onChange={(e) => updateAllocationAgreement(
                                                                    realIndex,
                                                                    'scope_type',
                                                                    e.target.value
                                                                )}
                                                            >
                                                                <Radio value="full_service">Toàn bộ dịch vụ</Radio>
                                                                <Radio value="partial_shared_work">Một phần việc chung</Radio>
                                                            </Radio.Group>
                                                        </Form.Item>
                                                    </Col> */}

                                                    {/* <Col xs={24} md={8}>
                                                        <Form.Item label="Doanh thu áp dụng">
                                                            <InputNumber
                                                                className="w-100"
                                                                min={0}
                                                                value={agreement.revenue_amount}
                                                                placeholder="Để trống = áp dụng toàn bộ doanh thu được phân bổ"
                                                                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                                onChange={(v) => updateAllocationAgreement(
                                                                    realIndex,
                                                                    'revenue_amount',
                                                                    v
                                                                )}
                                                            />
                                                        </Form.Item>
                                                    </Col> */}

                                                    <Col xs={24} md={12}>
                                                        <Form.Item label="Tổng tỷ lệ">
                                                            <Input
                                                                disabled
                                                                value={`${totalPercent}%`}
                                                                style={{
                                                                    color: Number(totalPercent) === 100 ? '#16a34a' : '#dc2626',
                                                                    fontWeight: 700,
                                                                }}
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>

                                                {group.employees.map((emp) => {
                                                    const row = (agreement.items || []).find(
                                                        (x) => Number(x.employee_id) === Number(emp.id)
                                                    );

                                                    return (
                                                        <Row gutter={[16, 0]} key={`${group.role_key}_${emp.id}`}>
                                                            <Col xs={12}>
                                                                <Form.Item label={group.label}>
                                                                    <Input disabled value={emp.display_name} />
                                                                </Form.Item>
                                                            </Col>

                                                            <Col xs={12}>
                                                                <Form.Item label="Tỷ lệ phân bổ (%)">
                                                                    <InputNumber
                                                                        className="w-100"
                                                                        min={0}
                                                                        max={100}
                                                                        value={row?.percent || 0}
                                                                        onChange={(v) => updateAllocationAgreementItem(
                                                                            realIndex,
                                                                            group.role_key,
                                                                            emp.id,
                                                                            v
                                                                        )}
                                                                    />
                                                                </Form.Item>
                                                            </Col>
                                                        </Row>
                                                    );
                                                })}

                                                <Form.Item label="Ghi chú cho thỏa thuận">
                                                    <Input.TextArea
                                                        rows={2}
                                                        value={agreement.note}
                                                        onChange={(e) => updateAllocationAgreement(
                                                            realIndex,
                                                            'note',
                                                            e.target.value
                                                        )}
                                                    />
                                                </Form.Item>

                                                <div style={{ textAlign: 'right' }}>
                                                    <Button
                                                        danger
                                                        onClick={() => removeAllocationAgreement(realIndex)}
                                                    >
                                                        Xóa thỏa thuận
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {agreements.length === 0 && (
                                        <Button
                                            type="dashed"
                                            onClick={() => addAllocationAgreement(group.role_key)}
                                        >
                                            + Thêm thỏa thuận {group.label.toLowerCase()}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}

                        {mode === 'work_unit' && group.allow_work_unit && (
                            <div>
                                <div style={{ fontWeight: 700, marginBottom: 12 }}>
                                    Phần việc thực tế để phân bổ doanh thu tính hoa hồng - {group.label}
                                </div>

                                <Row gutter={[16, 16]}>
                                    {group.employees.map((emp) => {
                                        const row = (item.work_units || []).find(
                                            (x) =>
                                                Number(x.employee_id) === Number(emp.id) &&
                                                x.role_key === group.role_key
                                        ) || {};

                                        return (
                                            <Col xs={24} key={`${group.role_key}_${emp.id}`}>
                                                <div style={styles.fieldBox}>
                                                    <Row gutter={[16, 0]}>
                                                        <Col xs={24} md={8}>
                                                            <Form.Item label={group.label}>
                                                                <Input disabled value={emp.display_name} />
                                                            </Form.Item>
                                                        </Col>

                                                        <Col xs={24} md={8}>
                                                            <Form.Item label="Loại phần việc">
                                                                <Radio.Group
                                                                    value={row.unit_type || 'session'}
                                                                    onChange={(e) => updateWorkUnit(
                                                                        group.role_key,
                                                                        emp.id,
                                                                        'unit_type',
                                                                        e.target.value
                                                                    )}
                                                                >
                                                                    <Radio value="session">Buổi</Radio>
                                                                    <Radio value="tooth">Răng</Radio>
                                                                    <Radio value="implant_post">Trụ</Radio>
                                                                </Radio.Group>
                                                            </Form.Item>
                                                        </Col>

                                                        <Col xs={24} md={8}>
                                                            <Form.Item label="Số lượng thực tế">
                                                                <InputNumber
                                                                    className="w-100"
                                                                    min={0}
                                                                    value={row.unit_value || 0}
                                                                    onChange={(v) => updateWorkUnit(
                                                                        group.role_key,
                                                                        emp.id,
                                                                        'unit_value',
                                                                        v
                                                                    )}
                                                                />
                                                            </Form.Item>
                                                        </Col>

                                                        {/* <Col xs={24} md={6}>
                                                            <Form.Item label="Phạm vi">
                                                                <Radio.Group
                                                                    value={row.revenue_scope || 'private'}
                                                                    onChange={(e) => updateWorkUnit(
                                                                        group.role_key,
                                                                        emp.id,
                                                                        'revenue_scope',
                                                                        e.target.value
                                                                    )}
                                                                >
                                                                    <Radio value="private">Phần riêng</Radio>
                                                                    <Radio value="shared">Phối hợp chung</Radio>
                                                                </Radio.Group>
                                                            </Form.Item>
                                                        </Col> */}
                                                    </Row>
                                                </div>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </div>
                        )}
                    </div>
                );
            })}
        </>
    );
}

export default CustomerServiceCommissionAllocationFields;
